import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { config } from './config'
import { cleanDesc } from './PredictionControls'
import { NumberField, confidenceBadge } from './ui'
import {
  type AvailableConfig, type AvailableValues, type BlacklistRule, type Block,
  type ConfigValue, type DataQuality, type EditLine,
} from './domain'

export function DataQualityBanner({ dq }: { dq: DataQuality }) {
  const pct = (dq.bto_ratio * 100).toFixed(0)
  return (
    <div className={`mb-5 border px-4 py-3 text-sm ${
      dq.low_bto
        ? 'border-black bg-bmw-paper text-black'
        : 'border-[#E5E7EC] bg-[#FAFBFC] text-[#444]'
    }`}>
      <span className="font-bold">
        {dq.bto_orders} BTO / {dq.total_orders} total orders ({pct}% customer-driven)
      </span>
      {dq.low_bto && (
        <span className="ml-2">
          — Low BTO ratio. Recommendation is influenced mainly by stock orders, not customer preference.
        </span>
      )}
    </div>
  )
}

// ── Plan control bar (editable order size + allocation status) ──────────────────

export function PlanControlBar({ planN, onPlanN, total, status, message, pinnedCount, editCount, onReset }: {
  planN: number
  onPlanN: (n: number) => void
  total: number
  status: string
  message: string
  pinnedCount: number
  editCount: number
  onReset: () => void
}) {
  const ok = status === 'ok'
  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 border border-[#E5E7EC] bg-[#FAFBFC] px-5 py-3" style={{ borderTop: '2px solid #92A2BD' }}>
      <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] text-[#444]">
        Order size
        <NumberField
          min={0} value={planN}
          onCommit={onPlanN}
          className="w-20 border border-[#CFD3DB] px-2 py-1 text-sm font-bold text-black tabular-nums"
        />
      </label>
      <span className={`px-2.5 py-1 text-xs font-bold uppercase tracking-[0.04em] ${ok ? 'bg-bmw-ocean text-white' : 'border border-black bg-white text-black'}`}>
        {total} / {planN} allocated
      </span>
      {pinnedCount > 0 && <span className="text-xs text-[#666] tabular-nums">{pinnedCount} pinned</span>}
      {!ok && <span className="text-xs font-bold text-black">⚠ {message}</span>}
      {editCount > 0 && (
        <button
          type="button" onClick={onReset}
          className="ml-auto text-xs font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:underline"
        >
          Reset edits ({editCount})
        </button>
      )}
    </div>
  )
}

// ── Block card ────────────────────────────────────────────────────────────────

function OptionTag({ code, descriptions }: { code: string; descriptions: Record<string, string> }) {
  const desc = descriptions[code]
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)

  if (!desc) return <span className="font-mono">{code}</span>

  return (
    <>
      <span
        className="font-mono cursor-default"
        onMouseEnter={e => {
          const r = e.currentTarget.getBoundingClientRect()
          setPos({ x: r.left + r.width / 2, y: r.top })
        }}
        onMouseLeave={() => setPos(null)}
      >
        {code}
      </span>
      {pos && createPortal(
        <div
          className="fixed z-50 pointer-events-none px-2 py-1 bg-black text-white text-xs whitespace-nowrap -translate-x-1/2 -translate-y-full -mt-1.5"
          style={{ left: pos.x, top: pos.y }}
        >
          {desc}
        </div>,
        document.body
      )}
    </>
  )
}

function OptionList({ codes, descriptions, className }: { codes: string[]; descriptions: Record<string, string>; className?: string }) {
  return (
    <span className={className}>
      {codes.map((c, i) => (
        <span key={c}>
          {i > 0 && <span className="text-[#CFD3DB] select-none"> · </span>}
          <OptionTag code={c} descriptions={descriptions} />
        </span>
      ))}
    </span>
  )
}

// Packages, click-to-expand: a package with known contents becomes a button that
// toggles an inline panel listing its option codes (hover still shows the name).
function PackageList({ codes, descriptions, openPkg, onToggle, hasContents }: {
  codes: string[]
  descriptions: Record<string, string>
  openPkg: string | null
  onToggle: (code: string) => void
  hasContents: (code: string) => boolean
}) {
  return (
    <span className="font-mono">
      {codes.map((c, i) => (
        <span key={c}>
          {i > 0 && <span className="text-[#CFD3DB] mx-1 select-none">·</span>}
          {hasContents(c) ? (
            <button
              type="button"
              onClick={() => onToggle(c)}
              className={`cursor-pointer hover:underline decoration-dotted underline-offset-2 ${openPkg === c ? 'text-[#1c69d4]' : ''}`}
            >
              <OptionTag code={c} descriptions={descriptions} />
              <span className="text-[10px] ml-0.5 select-none align-middle">{openPkg === c ? '▾' : '▸'}</span>
            </button>
          ) : (
            <OptionTag code={c} descriptions={descriptions} />
          )}
        </span>
      ))}
    </span>
  )
}

export function BlockCard({ block, lines, descriptions, packageContents, nmcMode, modelCodes, blacklist, quantities, allBlocks, onPin, onUnpin, onRemove, onAdd, onDeleteBlock, onBlockPin, onUnpinBlock, onSetAddons, onExplore }: {
  block: Block
  lines: EditLine[]
  descriptions: Record<string, string>
  packageContents: Record<string, string[]>
  nmcMode: boolean
  modelCodes: string[]
  blacklist: BlacklistRule[]
  quantities: Record<string, number>
  allBlocks: Block[]
  onPin: (id: string, qty: number) => void
  onUnpin: (id: string) => void
  onRemove: (id: string) => void
  onAdd: (blockRank: number, packages: string[], cand: AvailableConfig, blockShare: number) => void
  onDeleteBlock: (rank: number) => void
  onBlockPin: (rank: number, qty: number) => void
  onUnpinBlock: (rank: number) => void
  onSetAddons: (rank: number, options: string[], extras: string[]) => void
  onExplore?: (block: Block) => void
}) {
  const [showSteps, setShowSteps] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [openPkg, setOpenPkg] = useState<string | null>(null)
  const isUser = block.source === 'user'
  // Signatures of the OTHER blocks (packages + option set), to reject an option edit
  // that would duplicate an existing block.
  // NMC identity: edition (or legacy packages) + extra packages + single options.
  const addonSig = (b: Block) =>
    `${b.nmc_model_code ?? [...b.packages].sort().join('+')}#${[...(b.nmc_extra_packages ?? [])].sort().join('|')}#${[...b.single_options].sort().join('|')}`
  const otherSigs = allBlocks.filter(b => b.rank !== block.rank).map(addonSig)
  const blockPinned = block.pin != null
  // In NMC display, hide single options the edition already bundles (redundant legacy codes).
  const nmcSingles = nmcMode && block.nmc_model_code
    ? block.single_options.filter(o => !(block.nmc_included ?? []).includes(o))
    : block.single_options

  const blockTotal = lines.reduce((s, l) => s + (quantities[l.id] ?? 0), 0)
  // Hide model blocks squeezed to 0 cars (or with no rows). User-added blocks
  // always stay visible — including brand-new empty ones, so rows can be added.
  if (!isUser && (lines.length === 0 || blockTotal === 0)) return null
  // Show model rows only once they hold cars — so a low-share block reads as empty
  // and rows populate as cars flow in; user-added rows always stay visible.
  const visibleLines = lines.filter(l => l.source === 'user' || (quantities[l.id] ?? 0) >= 1)
  const present = new Set(lines.map(l => `${l.color ?? ''}|${l.interior ?? ''}|${l.rim ?? ''}|${l.trim ?? ''}`))

  const lowConf = !isUser && block.confidence_label === 'LOW'
  return (
    <div className="border border-[#E5E7EC] bg-white" style={{
      borderTop: '1px solid #92A2BD',
      ...(lowConf ? { borderLeft: '3px solid #C0392B' } : {}),
    }}>
      {/* Block header */}
      <div className="flex items-center justify-between gap-4 border-b border-[#E5E7EC] px-5 py-4 overflow-visible">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center bg-bmw-ocean text-xs font-bold text-white tabular-nums">
            {block.rank}
          </span>
          <div className="min-w-0">
            <div className="min-w-0">
              <p className="text-sm font-bold text-black flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {nmcMode && block.nmc_model_code ? (
                  <>
                    <span className="font-mono">{block.nmc_model_code}</span>
                    {block.nmc_extra_packages.length > 0 && (
                      <>
                        <span className="text-[#CFD3DB] font-normal select-none">+</span>
                        <OptionList codes={block.nmc_extra_packages} descriptions={descriptions} className="font-mono text-sm font-normal text-[#666]" />
                      </>
                    )}
                    {nmcSingles.length > 0 && (
                      <>
                        <span className="text-[#CFD3DB] font-normal select-none">+</span>
                        <OptionList codes={nmcSingles} descriptions={descriptions} className="text-sm font-normal text-[#666]" />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {block.packages.length > 0
                      ? <PackageList codes={block.packages} descriptions={descriptions}
                          openPkg={openPkg}
                          onToggle={c => setOpenPkg(p => (p === c ? null : c))}
                          hasContents={c => (packageContents[c]?.length ?? 0) > 0} />
                      : <span className="italic text-[#666]">No package</span>}
                    {block.rim_from_package && (
                      <span className="text-xs font-normal text-[#999]">(rim bundled)</span>
                    )}
                    {block.single_options.length > 0 && (
                      <>
                        <span className="text-[#CFD3DB] font-normal select-none">+</span>
                        <OptionList codes={block.single_options} descriptions={descriptions} className="text-sm font-normal text-[#666]" />
                      </>
                    )}
                  </>
                )}
              </p>
              {nmcMode && block.nmc_edition_name && (
                <p className="mt-0.5 text-xs text-[#999]">{block.nmc_edition_name}{block.nmc_edition_code && ` · ${block.nmc_edition_code}`}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {isUser
            ? <span className="border border-bmw-ocean bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-bmw-ocean">added</span>
            : confidenceBadge(block.confidence_label, block.overall_confidence)}
          {lines.length > 0 ? (
            <div className="flex items-center gap-1.5">
              <NumberField
                min={0} value={blockTotal}
                onCommit={v => onBlockPin(block.rank, v)}
                title="Cars in this block — editing locks the block total"
                className={`w-16 border px-1.5 py-1 text-right text-sm font-bold tabular-nums ${blockPinned ? 'border-bmw-ocean text-bmw-ocean' : 'border-[#E5E7EC] text-black'}`}
              />
              <span className="text-xs text-[#999]">cars</span>
              <input
                type="checkbox" checked={blockPinned}
                onChange={() => blockPinned ? onUnpinBlock(block.rank) : onBlockPin(block.rank, blockTotal)}
                title={blockPinned ? 'Locked block total — uncheck to let it flow' : 'Lock this block total'}
                className="h-3.5 w-3.5 cursor-pointer accent-bmw-ocean"
              />
            </div>
          ) : (
            <span className="text-sm font-bold text-black tabular-nums">0 cars</span>
          )}
          <span className="text-xs text-[#999] tabular-nums">{(block.weight_share * 100).toFixed(1)}%</span>
          {onExplore && (
            <button
              type="button" onClick={() => onExplore(block)} title="See how the model built this block"
              className="border border-[#CFD3DB] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-bmw-ocean hover:text-bmw-ocean"
            >
              Explore
            </button>
          )}
          <button
            type="button" onClick={() => onDeleteBlock(block.rank)} title="Delete block"
            className="text-[#CFD3DB] hover:text-black text-sm"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Base equipment + package drill-down — full width so they never crowd the header */}
      {(block.base_options.length > 0 || (openPkg && (packageContents[openPkg]?.length ?? 0) > 0)) && (
        <div className="space-y-1 border-b border-[#E5E7EC] px-5 py-2">
          {block.base_options.length > 0 && (
            <p className="text-xs leading-relaxed text-[#999]">
              Base: <OptionList codes={block.base_options} descriptions={descriptions} className="text-xs text-[#999]" />
            </p>
          )}
          {openPkg && (packageContents[openPkg]?.length ?? 0) > 0 && (
            <p className="border-l-2 border-[#E5E7EC] pl-2 text-xs leading-relaxed text-[#666]">
              <span className="font-mono">{openPkg}</span>
              <span className="text-[#999]"> includes </span>
              <OptionList codes={packageContents[openPkg]} descriptions={descriptions} className="text-xs text-[#666]" />
            </p>
          )}
        </div>
      )}

      {/* Config table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-bmw-ocean bg-[#FAFBFC] text-[10px] font-bold uppercase tracking-[0.12em] text-bmw-ocean">
              <th className="px-5 py-2.5 text-left">Color</th>
              <th className="px-5 py-2.5 text-left">Interior</th>
              <th className="px-5 py-2.5 text-left">Trim</th>
              <th className="px-5 py-2.5 text-left">Rim</th>
              <th className="px-5 py-2.5 text-right">Qty</th>
              <th className="px-5 py-2.5 text-right">Share</th>
              <th className="px-3 py-2.5 text-center">Locked</th>
              <th className="px-2 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {visibleLines.map(l => {
              const q = quantities[l.id] ?? 0
              const pinned = l.pin !== null
              return (
                <tr key={l.id} className={`border-b border-[#E5E7EC] hover:bg-[#FAFBFC] last:border-b-0 ${pinned ? 'bg-[#F3F6FB]' : ''}`}>
                  <td className="px-5 py-2.5 font-mono text-xs">
                    {l.color ? <OptionTag code={l.color} descriptions={descriptions} /> : '—'}
                    {l.source === 'user' && <span className="ml-1.5 not-italic text-[9px] font-bold uppercase tracking-[0.06em] text-bmw-ocean">added</span>}
                  </td>
                  <td className="px-5 py-2.5 font-mono text-xs">{l.interior ? <OptionTag code={l.interior} descriptions={descriptions} /> : '—'}</td>
                  <td className="px-5 py-2.5 font-mono text-xs">{l.trim ? <OptionTag code={l.trim} descriptions={descriptions} /> : '—'}</td>
                  <td className="px-5 py-2.5 font-mono text-xs">{l.rim ? <OptionTag code={l.rim} descriptions={descriptions} /> : '—'}</td>
                  <td className="px-5 py-2.5 text-right">
                    <NumberField
                      min={0} value={q}
                      onCommit={v => onPin(l.id, v)}
                      className={`w-16 border px-1.5 py-1 text-right text-sm font-bold tabular-nums ${pinned ? 'border-bmw-ocean text-bmw-ocean' : 'border-[#E5E7EC] text-black'}`}
                    />
                  </td>
                  <td className="px-5 py-2.5 text-right text-[#999] tabular-nums">
                    {blockTotal > 0 ? ((q / blockTotal) * 100).toFixed(0) : 0}%
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <input
                      type="checkbox" checked={pinned}
                      onChange={() => pinned ? onUnpin(l.id) : onPin(l.id, q)}
                      title={pinned ? 'Locked at this quantity — uncheck to let it flow' : 'Lock this quantity'}
                      className="h-3.5 w-3.5 cursor-pointer accent-bmw-ocean"
                    />
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <button
                      type="button" onClick={() => onRemove(l.id)} title="Remove configuration"
                      className="text-[#CFD3DB] hover:text-black text-xs"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Add configuration / edit options */}
      <div className="border-t border-[#E5E7EC] px-5 py-3">
        {showAdd ? (
          <AddConfigPanel
            modelCodes={modelCodes} packages={block.packages} descriptions={descriptions} present={present}
            blacklist={blacklist}
            onClose={() => setShowAdd(false)}
            onAdd={cand => { onAdd(block.rank, block.packages, cand, blockTotal); setShowAdd(false) }}
          />
        ) : showOptions ? (
          <AddonsPanel
            modelCodes={modelCodes} packages={block.packages} descriptions={descriptions}
            blacklist={blacklist} nmcModelCode={block.nmc_model_code}
            currentOptions={block.single_options} currentExtras={block.nmc_extra_packages ?? []}
            isDuplicate={(opts, extras) => otherSigs.includes(
              `${block.nmc_model_code ?? [...block.packages].sort().join('+')}#${[...extras].sort().join('|')}#${[...opts].sort().join('|')}`)}
            onClose={() => setShowOptions(false)}
            onApply={(opts, extras) => { onSetAddons(block.rank, opts, extras); setShowOptions(false) }}
          />
        ) : (
          <div className="flex items-center gap-4">
            <button
              type="button" onClick={() => setShowAdd(true)}
              className="text-xs font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:underline"
            >
              + Add configuration
            </button>
            <button
              type="button" onClick={() => setShowOptions(true)}
              className="text-xs font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:underline"
            >
              ± Edit add-ons
            </button>
          </div>
        )}
      </div>

      {/* Step confidences */}
      {block.step_confidences.length > 0 && (
      <div className="border-t border-[#E5E7EC] px-5 py-3">
        <button
          onClick={() => setShowSteps(v => !v)}
          className="text-xs text-[#999] hover:text-[#444] font-bold uppercase tracking-[0.06em]"
        >
          {showSteps ? '▲ Hide' : '▼ Show'} step confidences
        </button>
        {showSteps && (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {block.step_confidences.map(sc => (
              <div key={sc.step} className="border border-[#E5E7EC] bg-[#FAFBFC] p-3 text-xs">
                <p className="mb-1 font-bold uppercase tracking-[0.06em] text-[#444]">{sc.step}</p>
                {sc.rim_from_package
                  ? <p className="text-[#999] italic">bundled in package</p>
                  : <>
                      <p className="text-[#999]">ESS {sc.ess.toFixed(0)}</p>
                      <p className="font-bold text-black">
                        {(sc.confidence * 100).toFixed(0)}%
                      </p>
                    </>
                }
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  )
}

// ── Add-ons panel ───────────────────────────────────────────────────────────────
// Add/remove everything on the car beyond the edition — extra packages (constraint-
// filtered per the pricelist) and single options — in one list. Stored back into the
// right fields (packages vs options) so the Excel keeps the PAKET / EXTRA OPTIONS split.
// Keeps the block's rows and car count; refuses an edit that duplicates another block.
type AddonRow = { code: string; kind: 'package' | 'option'; count: number; weight_share: number }
function AddonsPanel({ modelCodes, packages, descriptions, blacklist, nmcModelCode, currentOptions, currentExtras, isDuplicate, onClose, onApply }: {
  modelCodes: string[]
  packages: string[]
  descriptions: Record<string, string>
  blacklist: BlacklistRule[]
  nmcModelCode: string | null
  currentOptions: string[]
  currentExtras: string[]
  isDuplicate: (options: string[], extras: string[]) => boolean
  onClose: () => void
  onApply: (options: string[], extras: string[]) => void
}) {
  const [data, setData] = useState<AddonRow[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [selOpts, setSelOpts] = useState<Set<string>>(new Set(currentOptions))
  const [selPkgs, setSelPkgs] = useState<Set<string>>(new Set(currentExtras))
  const [query, setQuery] = useState('')

  useEffect(() => {
    fetch(`${config.apiUrl}/api/bundle-options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model_codes: modelCodes, packages, blacklist, nmc_model_code: nmcModelCode }),
    })
      .then(r => r.json())
      .then(d => {
        if (!Array.isArray(d?.options)) { setErr(d?.detail || 'Could not load add-ons'); return }
        const rows: AddonRow[] = [
          ...(d.extra_packages ?? []).map((p: { code: string; count: number }) => ({ code: p.code, kind: 'package' as const, count: p.count, weight_share: 0 })),
          ...d.options.map((o: { code: string; count: number; weight_share: number }) => ({ code: o.code, kind: 'option' as const, count: o.count, weight_share: o.weight_share })),
        ]
        setData(rows)
      })
      .catch(() => setErr('Could not load add-ons'))
  }, [])

  const label = (code: string) => {
    const d = cleanDesc(code, descriptions[code] ?? null)
    return d ? `${code} · ${d}` : code
  }

  if (err) return <div className="text-xs text-black">⚠ {err} <button onClick={onClose} className="ml-2 underline">close</button></div>
  if (!data) return <div className="text-xs text-[#999]">Loading add-ons…</div>

  // Keep any current add-on missing from the candidate list so it can still be removed.
  const known = new Set(data.map(r => r.code))
  const allRows: AddonRow[] = [
    ...data,
    ...currentExtras.filter(c => !known.has(c)).map(c => ({ code: c, kind: 'package' as const, count: 0, weight_share: 0 })),
    ...currentOptions.filter(c => !known.has(c)).map(c => ({ code: c, kind: 'option' as const, count: 0, weight_share: 0 })),
  ]
  const q = query.trim().toLowerCase()
  const rows = q
    ? allRows.filter(r => r.code.toLowerCase().includes(q) || (cleanDesc(r.code, descriptions[r.code] ?? null) ?? '').toLowerCase().includes(q))
    : allRows
  const isOn = (r: AddonRow) => (r.kind === 'package' ? selPkgs : selOpts).has(r.code)
  const toggle = (r: AddonRow) => {
    const [get, set] = r.kind === 'package' ? [selPkgs, setSelPkgs] as const : [selOpts, setSelOpts] as const
    const n = new Set(get); n.has(r.code) ? n.delete(r.code) : n.add(r.code); set(n)
  }
  const chosenOpts = [...selOpts].sort()
  const chosenPkgs = [...selPkgs].sort()
  const dup = isDuplicate(chosenOpts, chosenPkgs)

  return (
    <div className="border border-[#E5E7EC] bg-[#FAFBFC] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.06em] text-[#444]">Edit add-ons</span>
        <span className="text-[10px] text-[#999]">{allRows.length} available · click to toggle</span>
      </div>
      {allRows.length > 0 && (
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search packages & options…"
          className="mb-2 w-full border border-[#CFD3DB] bg-white px-2 py-1.5 text-xs focus:border-bmw-ocean focus:outline-none"
        />
      )}
      <div className="mb-3 flex max-h-64 flex-col gap-1 overflow-y-auto">
        {allRows.length === 0 && <p className="text-xs text-[#999]">No add-ons available for this edition.</p>}
        {allRows.length > 0 && rows.length === 0 && <p className="text-xs text-[#999]">No add-ons match “{query}”.</p>}
        {rows.map(r => {
          const on = isOn(r)
          return (
            <button
              key={`${r.kind}:${r.code}`} type="button" onClick={() => toggle(r)}
              className={`flex items-center justify-between gap-3 border px-2 py-1.5 text-left text-xs ${on ? 'border-bmw-ocean bg-white' : 'border-[#E5E7EC] bg-white hover:border-[#CFD3DB]'}`}
            >
              <span className="min-w-0 truncate font-mono">
                <span className={`mr-1.5 ${on ? 'text-bmw-ocean' : 'text-[#CFD3DB]'}`}>{on ? '✓' : '+'}</span>
                {label(r.code)}
                <span className="ml-1.5 rounded-sm bg-[#E8EBF1] px-1 py-0.5 text-[9px] font-bold uppercase not-italic tracking-[0.04em] text-[#666]">{r.kind === 'package' ? 'pkg' : 'opt'}</span>
              </span>
              <span className="flex-shrink-0 tabular-nums text-[#999]">
                {r.count ? `${r.count} order${r.count === 1 ? '' : 's'}` : 'new'}
              </span>
            </button>
          )
        })}
      </div>
      {dup && <p className="mb-2 text-xs text-black">⚠ Another block already has this exact spec.</p>}
      <div className="flex items-center gap-2">
        <button
          type="button" disabled={dup} onClick={() => onApply(chosenOpts, chosenPkgs)}
          className="bg-bmw-ocean px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-white disabled:opacity-30"
        >
          Apply
        </button>
        <button type="button" onClick={onClose} className="text-xs text-[#999] hover:text-black">Cancel</button>
      </div>
    </div>
  )
}

// ── Add-configuration panel ─────────────────────────────────────────────────────

function AddConfigPanel({ modelCodes, packages, descriptions, present, blacklist, onClose, onAdd }: {
  modelCodes: string[]
  packages: string[]
  descriptions: Record<string, string>
  present: Set<string>
  blacklist: BlacklistRule[]
  onClose: () => void
  onAdd: (cand: AvailableConfig) => void
}) {
  const [data, setData] = useState<{ configurations: AvailableConfig[]; available: AvailableValues } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [color, setColor] = useState('')
  const [interior, setInterior] = useState('')
  const [rim, setRim] = useState('')
  const [trim, setTrim] = useState('')

  useEffect(() => {
    fetch(`${config.apiUrl}/api/configurations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model_codes: modelCodes, packages, blacklist }),
    })
      .then(r => r.json())
      .then(d => setData({
        configurations: d.configurations ?? [],
        available: d.available ?? { colors: [], interiors: [], rims: [], trims: [] },
      }))
      .catch(() => setErr('Could not load configurations'))
  }, [])

  const av = data?.available
  const configs = data?.configurations ?? []

  // Order history for the exact composed tuple, if it was ever ordered (drives the
  // support hint and the starting weight; a brand-new combination has neither).
  const composed = !!(color && interior && rim)
  const match = composed
    ? configs.find(c => (c.color ?? '') === color && (c.interior ?? '') === interior
        && (c.rim ?? '') === rim && (c.trim ?? '') === trim)
    : undefined
  const alreadyPresent = composed && present.has(`${color}|${interior}|${rim}|${trim}`)
  const ready = composed && !alreadyPresent

  const label = (code: string, desc: string | null) => {
    const d = cleanDesc(code, desc ?? descriptions[code] ?? null)
    return d ? `${code} · ${d}` : code
  }
  const optLabel = (v: ConfigValue) =>
    `${label(v.code, v.description)}  ·  ${v.count ? `${v.count} order${v.count === 1 ? '' : 's'}` : 'new'}`
  const selCls = 'border border-[#CFD3DB] bg-white px-2 py-1.5 text-xs font-mono min-w-0 flex-1'

  if (err) return <div className="text-xs text-black">⚠ {err} <button onClick={onClose} className="ml-2 underline">close</button></div>
  if (!av) return <div className="text-xs text-[#999]">Loading configurations…</div>

  return (
    <div className="border border-[#E5E7EC] bg-[#FAFBFC] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.06em] text-[#444]">Add configuration</span>
        <span className="text-[10px] text-[#999]">any combination · counts show order history</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select className={selCls} value={color} onChange={e => setColor(e.target.value)}>
          <option value="">Color…</option>
          {av.colors.map(v => <option key={v.code} value={v.code}>{optLabel(v)}</option>)}
        </select>
        <select className={selCls} value={interior} onChange={e => setInterior(e.target.value)}>
          <option value="">Interior…</option>
          {av.interiors.map(v => <option key={v.code} value={v.code}>{optLabel(v)}</option>)}
        </select>
        <select className={selCls} value={rim} onChange={e => setRim(e.target.value)}>
          <option value="">Rim…</option>
          {av.rims.map(v => <option key={v.code} value={v.code}>{optLabel(v)}</option>)}
        </select>
        <select className={selCls} value={trim} onChange={e => setTrim(e.target.value)}>
          <option value="">Trim…</option>
          {av.trims.map(v => <option key={v.code} value={v.code}>{optLabel(v)}</option>)}
        </select>
        <button
          type="button" disabled={!ready}
          onClick={() => onAdd({
            color: color || null, interior: interior || null, rim: rim || null, trim: trim || null,
            weight_share: match?.weight_share ?? 0, count: match?.count ?? 0,
          })}
          className="bg-bmw-ocean px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-white disabled:opacity-30"
        >
          Add
        </button>
        <button type="button" onClick={onClose} className="text-xs text-[#999] hover:text-black">Cancel</button>
      </div>
      {composed && (
        <p className="mt-2 text-[11px] text-[#999]">
          {alreadyPresent
            ? 'Already in this block.'
            : match
              ? `Historical share ${(match.weight_share * 100).toFixed(1)}% · ${match.count} order${match.count === 1 ? '' : 's'}`
              : 'New combination — not seen in orders. Adds with 1 car; adjust as needed.'}
        </p>
      )}
    </div>
  )
}

// ── Add-block panel ─────────────────────────────────────────────────────────────
